/* Server-rendered option values, rather than a truncated all-variants JSON blob. */
(() => {
  'use strict';
  const Portal = window.Portal;
  if (!Portal) return;
  const requests = new WeakMap();
  const pending = new WeakMap();
  document.addEventListener('change', async event => {
    const input = event.target.closest('[data-option-value-id]');
    const section = input?.closest('[data-product-section]');
    if (!section) return;
    const siblingUrl = input.dataset.productUrl;
    if (siblingUrl && new URL(siblingUrl, location.origin).pathname !== new URL(section.dataset.productUrl, location.origin).pathname) {
      // Full navigation deliberately preserves the correct template and app lifecycle for combined listings.
      location.assign(siblingUrl);
      return;
    }
    requests.get(section)?.abort();
    const controller = new AbortController(); requests.set(section, controller);
    const selected = [...section.querySelectorAll('[data-option-value-id]:checked')].map(el => el.dataset.optionValueId);
    const url = new URL(section.dataset.productUrl, location.origin);
    // Keep Shopify's unpublished-theme preview and locale context.
    const page = new URL(location.href);
    for (const key of ['preview_theme_id', '_fd', 'pb', 'view']) if (page.searchParams.has(key)) url.searchParams.set(key, page.searchParams.get(key));
    url.searchParams.set('option_values', selected.join(','));
    url.searchParams.set('section_id', section.dataset.sectionId);
    const focusId = input.id;
    const quantity = section.querySelector('input[name="quantity"]')?.value;
    const plan = section.querySelector('select[name="selling_plan"]')?.value;
    section.setAttribute('aria-busy', 'true');
    section.querySelectorAll('[data-add], .shopify-payment-button button').forEach(button => { button.disabled = true; });
    const idField = section.querySelector('[data-variant-id]'); if (idField) idField.disabled = true;
    Portal.setError(section, '');
    const previousError = section.querySelector('[data-variant-error]'); if (previousError) { previousError.hidden = true; previousError.textContent = ''; }
    pending.set(section, true);
    try {
      const response = await Portal.request(url, { signal: controller.signal });
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (requests.get(section) !== controller || controller.signal.aborted) return;
      const next = documentFragment.getElementById(section.id);
      if (!next) throw new Error('The selected product options could not be loaded.');
      const variant = JSON.parse(next.querySelector('[data-selected-variant]')?.textContent || 'null');
      if (!variant) throw new Error('The selected product options could not be loaded.');
      section.querySelectorAll('[data-variant-part]').forEach(part => {
        const replacement = next.querySelector(`[data-variant-part="${CSS.escape(part.dataset.variantPart)}"]`);
        if (replacement) part.innerHTML = replacement.innerHTML;
      });
      if (idField) { idField.value = variant.id || ''; idField.disabled = !variant.available || !variant.id; }
      section.querySelector('[data-selected-variant]').textContent = JSON.stringify(variant);
      section.querySelectorAll('.shopify-payment-button button').forEach(button => { button.disabled = !variant.available || !variant.id; });
      section.querySelectorAll('[data-product-visibility]').forEach(block => {
        const state = block.dataset.productVisibility;
        const visible = { in_stock: !!variant.productAvailable, out_of_stock: !variant.productAvailable, variant_in_stock: !!variant.available, variant_out_of_stock: !variant.available, variant_unavailable: !variant.id, out_of_stock_continue: !!variant.continuedSelling };
        block.hidden = Object.prototype.hasOwnProperty.call(visible, state) ? !visible[state] : false;
      });
      const qty = section.querySelector('input[name="quantity"]');
      if (qty && quantity) {
        const min = Number(qty.min || 1), step = Number(qty.step || 1), max = qty.max ? Number(qty.max) : Infinity;
        qty.value = String(Math.min(max, min + Math.max(0, Math.round((Number(quantity) - min) / step)) * step));
      }
      const planSelect = section.querySelector('select[name="selling_plan"]');
      if (planSelect && [...planSelect.options].some(option => option.value === plan)) planSelect.value = plan;
      Portal.selectMedia(section.querySelector('[data-gallery]'), variant.mediaId);
      page.searchParams.delete('option_values');
      if (variant.id) page.searchParams.set('variant', variant.id);
      else { page.searchParams.delete('variant'); page.searchParams.set('option_values', selected.join(',')); }
      history.replaceState({}, '', page);
      document.getElementById(focusId)?.focus({ preventScroll: true });
      idField?.dispatchEvent(new Event('change', { bubbles: true }));
      section.dispatchEvent(new CustomEvent('wgp:variant:change', { bubbles: true, detail: { variant, sectionId: section.dataset.sectionId } }));
      if (planSelect) planSelect.dispatchEvent(new Event('change', { bubbles: true }));
      Portal.announce(section.querySelector('[data-variant-part="stock"]')?.textContent.trim() || 'Product options updated.');
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Never re-enable a stale variant ID after a failed option request.
        const errorElement = section.querySelector('[data-variant-error]');
        if (errorElement) { errorElement.hidden = false; errorElement.textContent = Portal.config.variantError || error.message; }
      }
    } finally {
      if (requests.get(section) === controller) {
        pending.set(section, false); section.removeAttribute('aria-busy');
      }
    }
  });
  document.addEventListener('submit', event => {
    const section = event.target.closest('[data-product-section]');
    if (section && pending.get(section)) event.preventDefault();
  }, true);
})();
